import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { User } from '../lib/auth';
import { FaArrowLeft, FaWallet, FaPlus, FaHistory, FaExchangeAlt, FaMoneyBillWave, FaCoins } from 'react-icons/fa';
import MoneyFall from '../components/MoneyFall';

export default function Wallet() {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
    loadRecentTransactions();
    checkPaymentStatus();
  }, [location]);

  const loadUserData = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) return;
      const user: User = JSON.parse(userJson);
      setBalance(user.wallet_balance);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadRecentTransactions = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) return;
      const user: User = JSON.parse(userJson);

      const { data, error } = await supabase
        .from('stripetransactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentTransactions(data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const checkPaymentStatus = async () => {
    const query = new URLSearchParams(location.search);
    const success = query.get('success');
    const sessionId = query.get('session_id');
    const amount = query.get('amount');

    if (success === 'true' && sessionId && amount) {
      try {
        const response = await fetch(`/api/verify-payment/${sessionId}`);
        const data = await response.json();

        if (data.status === 'paid') {
          await processSuccessfulPayment(parseFloat(amount));
          toast.success('Payment successful!');
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
        } else {
          toast.error('Payment verification failed.');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        toast.error('Payment verification failed.');
      } finally {
        // Remove the query params from the URL
        navigate('/wallet', { replace: true });
      }
    }
  };

  const processSuccessfulPayment = async (paymentAmount: number) => {
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) throw new Error('User not authenticated');
      const user: User = JSON.parse(userJson);

      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: paymentAmount,
          type: 'topup',
          status: 'completed'
        });

      if (transactionError) throw transactionError;

      const newBalance = user.wallet_balance + paymentAmount;

      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ wallet_balance: newBalance })
        .eq('id', user.id);

      if (updateError) throw updateError;

      user.wallet_balance = newBalance;
      localStorage.setItem('user', JSON.stringify(user));

      setBalance(newBalance);
      loadRecentTransactions();
      setAmount('');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleTopUp = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseFloat(amount) }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'topup':
        return <FaPlus className="text-green-500" />;
      case 'withdraw':
        return <FaMoneyBillWave className="text-red-500" />;
      case 'transfer':
        return <FaExchangeAlt className="text-blue-500" />;
      default:
        return <FaCoins className="text-yellow-500" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'topup':
        return 'text-green-500';
      case 'withdraw':
        return 'text-red-500';
      case 'transfer':
        return 'text-blue-500';
      default:
        return 'text-yellow-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {showConfetti && <MoneyFall />}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Floating Back Button */}
        <Link
          to="/dashboard"
          className="fixed top-20 left-4 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-105 shadow-lg"
        >
          <FaArrowLeft className="w-5 h-5" />
        </Link>

        {/* Header */}
        <div className="text-center mb-12 relative">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 mb-2">
            Digital Wallet
          </h1>
          <p className="text-lg text-white/70">Manage your funds with ease</p>
          
          {/* Animated Background Element */}
          <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-3xl animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Balance Card */}
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-3xl backdrop-blur-xl border border-white/20 shadow-2xl group">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-purple-900/80 to-pink-900/80 z-0"></div>
              
              {/* Animated Wave */}
              <div className="absolute bottom-0 left-0 right-0 h-32 z-0">
                <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    className="animate-wave-slow fill-white/5" 
                    fillOpacity="1" 
                    d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,224C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
                  </path>
                </svg>
                <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    className="animate-wave fill-white/10" 
                    fillOpacity="1" 
                    d="M0,96L48,128C96,160,192,224,288,213.3C384,203,480,117,576,117.3C672,117,768,203,864,202.7C960,203,1056,117,1152,117.3C1248,117,1344,203,1392,245.3L1440,288L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
                  </path>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10 p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
                    <FaWallet className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white/80">Current Balance</h2>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-white group-hover:text-yellow-300 transition-colors duration-300">
                        ${balance.toFixed(2)}
                      </span>
                      <span className="ml-2 text-white/60">USD</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 space-y-6">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Amount to Top Up
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <span className="text-white/60">$</span>
                      </div>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="block w-full pl-10 pr-12 py-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center">
                        <button
                          onClick={() => setAmount('100')}
                          className="h-full px-3 text-sm text-white/60 hover:text-white transition-colors"
                        >
                          $100
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleTopUp}
                    disabled={loading}
                    className="relative w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] overflow-hidden group"
                  >
                    {/* Button Background Pulse */}
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-30 group-active:opacity-50 transition-opacity duration-300"></span>
                    
                    {/* Button Content */}
                    <span className="relative flex items-center justify-center">
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FaPlus className="mr-2" />
                          Top Up Wallet
                        </>
                      )}
                    </span>
                  </button>
                </div>
                
                {/* Quick Actions */}
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <Link
                    to="/transfer"
                    className="flex items-center justify-center py-3 px-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
                  >
                    <FaExchangeAlt className="mr-2" />
                    <span>Transfer</span>
                  </Link>
                  <Link
                    to="/withdraw"
                    className="flex items-center justify-center py-3 px-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
                  >
                    <FaMoneyBillWave className="mr-2" />
                    <span>Withdraw</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Transactions Card */}
          <div className="lg:col-span-1">
            <div className="h-full rounded-3xl backdrop-blur-xl border border-white/20 shadow-2xl overflow-hidden">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center">
                  <FaHistory className="w-5 h-5 text-purple-400 mr-2" />
                  <h2 className="text-xl font-semibold text-white">Recent Activity</h2>
                </div>
                <button 
                  onClick={loadRecentTransactions}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 h-[calc(100%-76px)] overflow-auto">
                {recentTransactions.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-4 animate-pulse">
                      <FaCoins className="w-10 h-10 text-yellow-400 opacity-70" />
                    </div>
                    <p className="text-white/60 mb-2">No recent transactions</p>
                    <p className="text-sm text-white/40">Top up your wallet to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentTransactions.map((transaction, index) => (
                      <div 
                        key={transaction.id} 
                        className="relative bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all duration-300 transform hover:scale-[1.02] animate-fade-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-3">
                            {getTransactionIcon(transaction.type)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-white capitalize">{transaction.type}</p>
                            <p className="text-xs text-white/60">{formatDate(transaction.created_at)}</p>
                          </div>
                          <div className={`text-right ${getTransactionColor(transaction.type)}`}>
                            <p className="font-bold">
                              {transaction.type === 'topup' ? '+' : '-'}${transaction.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-white/60 capitalize">{transaction.status}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes wave {
          0% {
            transform: translateX(0) translateZ(0) scaleY(1);
          }
          50% {
            transform: translateX(-25%) translateZ(0) scaleY(0.8);
          }
          100% {
            transform: translateX(-50%) translateZ(0) scaleY(1);
          }
        }
        
        @keyframes wave-slow {
          0% {
            transform: translateX(0) translateZ(0) scaleY(1);
          }
          50% {
            transform: translateX(-25%) translateZ(0) scaleY(0.8);
          }
          100% {
            transform: translateX(-50%) translateZ(0) scaleY(1);
          }
        }
        
        .animate-wave {
          animation: wave 15s linear infinite;
          animation-fill-mode: forwards;
          transform-origin: center bottom;
        }
        
        .animate-wave-slow {
          animation: wave-slow 20s linear infinite;
          animation-fill-mode: forwards;
          transform-origin: center bottom;
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}